import {
  Button,
  closeCurrentDialog,
  DataStatus,
  FormControl,
  openDialog,
  Search,
  showNotification,
  Table,
  TableRow,
} from 'platform/components';
import {Box, Heading, HStack, Text, VStack} from 'platform/foundation';
import {useCurrencySymbolFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';

import {useEffect, useState} from 'react';
import {useFieldArray, UseFormReturn, useWatch} from 'react-hook-form';

import {isEmpty, isNil, values} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {getPriceWithVat, useTenant, useVatRatesOptions} from '@dms/shared';

import {FormValues} from '../types/FormValues';
import {PriceListItem} from '../types/PriceListItem';
import {getPriceListColumns} from '../utils/getPriceListColumns';
import {PriceListItemForm} from './PriceListItemForm';

interface PriceListProps {
  control: FormControl<FormValues>;
  formApi: UseFormReturn<FormValues>;
}

export function PriceList(props: PriceListProps) {
  const {tenantCurrency} = useTenant();
  const formatCurrencySymbol = useCurrencySymbolFormatter();
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const [vatRates] = useVatRatesOptions();

  const selectedVatType = useWatch({control: props.control, name: 'vatType'});
  const vatRate = vatRates.find((vatRate) => vatRate.value === selectedVatType);

  const [searchString, setSearchString] = useState<string | null>(null);

  const {fields, append, remove, update} = useFieldArray({
    name: 'priceList',
    control: props.control,
    keyName: 'id',
  });

  const handleAddNewPriceListItem = () =>
    openDialog(
      <PriceListItemForm
        items={fields.map((field, index) => ({...field, originalIndex: index}))}
        vatRate={{
          label: vatRate?.fieldLabel ?? '',
          value: Number(vatRate?.rate) ?? 0,
        }}
        onSubmit={async (data) => {
          await append(data);
          closeCurrentDialog();
        }}
      />,
      {
        title: i18n.t('entity.rental.labels.newPriceList'),
      }
    );

  const handleEditPriceListItem = (field: PriceListItem & {originalIndex: number}) => {
    openDialog(
      <PriceListItemForm
        defaultValues={field}
        items={fields.map((field, index) => ({...field, originalIndex: index}))}
        vatRate={{
          label: vatRate?.fieldLabel ?? '',
          value: Number(vatRate?.rate) ?? 0,
        }}
        onSubmit={async (data) => {
          await update(field.originalIndex, data);
          closeCurrentDialog();
        }}
      />,
      {
        title: i18n.t('entity.rental.labels.editPriceList'),
      }
    );
  };

  const filteredAndSortedFields = fields
    .map((field, index) => ({...field, originalIndex: index}))
    .filter((item) => {
      if (isNil(searchString) || isEmpty(searchString)) {
        return true;
      }

      return values(item).some((value) => value.toString().includes(searchString));
    })
    .sort((a, b) => a.from - b.from);

  useEffect(() => {
    const newVatRate = Number(vatRate?.rate ?? '0');

    // handles invalid vatRate
    if (isNilOrEmpty(newVatRate) || isNaN(newVatRate)) {
      return;
    }

    const isSomeItemWithInvalidVatRate = fields.some((field) => {
      const priceWithVat = getPriceWithVat(String(field.rate.withoutVat), newVatRate);

      return priceWithVat !== field.rate.withVat;
    });

    // skips when there is nothing to update
    if (!isSomeItemWithInvalidVatRate || isNilOrEmpty(fields)) {
      return;
    }

    showNotification.info(i18n.t('entity.rental.labels.vatRateChanged'));

    fields.forEach((field, index) =>
      update(index, {
        ...field,
        rate: {
          withoutVat: field.rate.withoutVat,
          withVat: getPriceWithVat(String(field.rate.withoutVat), newVatRate),
        },
        ratePerKilometer: {
          withoutVat: field.ratePerKilometer.withoutVat,
          withVat: getPriceWithVat(String(field.ratePerKilometer.withoutVat), newVatRate),
        },
      })
    );
    // removed fields and update to prevent render-loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vatRate]);

  return (
    <VStack maxWidth="100%" spacing={4}>
      <Heading size={4}>{i18n.t('entity.rental.labels.priceList')}</Heading>

      <DataStatus
        isEmpty={isEmpty(filteredAndSortedFields)}
        minHeight={50}
        action={{
          title: i18n.t('entity.rental.labels.newPriceList'),
          leftIcon: 'content/add_circle',
          onClick: handleAddNewPriceListItem,
        }}
      >
        <HStack justify="space-between" width="100%" align="center">
          <Search value={searchString} onChange={setSearchString} />

          <Button
            variant="link"
            title={i18n.t('entity.rental.labels.newPriceList')}
            leftIcon="content/add_circle"
            onClick={handleAddNewPriceListItem}
          />
        </HStack>

        <Table
          columns={getPriceListColumns(formatCurrencySymbol(tenantCurrency))}
          variant="bordered"
        >
          {filteredAndSortedFields.map((field) => (
            <TableRow
              key={field.id}
              onRowClick={() => handleEditPriceListItem(field)}
              actionsWidth="5%"
              actions={{
                primary: [
                  {
                    icon: 'image/edit',
                    onClick: () => handleEditPriceListItem(field),
                    title: i18n.t('general.actions.edit'),
                  },
                  {
                    icon: 'action/delete',
                    onClick: () => remove(field.originalIndex),
                    title: i18n.t('general.actions.remove'),
                    severity: 'danger',
                  },
                ],
              }}
            >
              <Box padding={2}>
                <Text alternative>{field.name}</Text>
              </Box>
              <Box padding={2}>
                <Text>{formatNumber(field.from)}</Text>
              </Box>
              <Box padding={2}>
                <Text>{formatNumber(field.to)}</Text>
              </Box>
              <Box padding={2}>
                <Text>{i18n.t(`entity.rental.labels.${field.period}`)}</Text>
              </Box>
              <Box padding={2}>
                <Text>{formatCurrency(field.rate.withoutVat, tenantCurrency, 2)}</Text>
              </Box>
              <Box padding={2}>
                <Text>{formatCurrency(field.rate.withVat, tenantCurrency, 2)}</Text>
              </Box>
              <Box padding={2}>
                <Text>{formatCurrency(field.ratePerKilometer.withoutVat, tenantCurrency, 2)}</Text>
              </Box>
              <Box padding={2}>
                <Text>{formatCurrency(field.ratePerKilometer.withVat, tenantCurrency, 2)}</Text>
              </Box>
              <Box padding={2}>
                <Text>{formatNumber(field.includedKilometers)} Km</Text>
              </Box>
            </TableRow>
          ))}
        </Table>
      </DataStatus>
    </VStack>
  );
}
